/**
 * Pest and Disease Prediction Service
 * 
 * Implements pest/disease prediction as per PRD FR-006:
 * - 14-30 day advance predictions
 * - Weather-based risk modeling
 * - Risk level classification
 * - Alert generation
 * - Geographic risk mapping
 * 
 * Reference: PRD Section 5.1, Feature 6
 */

/**
 * Risk Level Classifications per PRD
 */
export const RISK_LEVELS = {
  HIGH: {
    level: 'High',
    color: '#dc2626',
    threshold: 70,
    description: 'Immediate preventive action required',
    alertPriority: 'critical'
  },
  MODERATE: {
    level: 'Moderate',
    color: '#f59e0b',
    threshold: 40,
    description: 'Increased monitoring recommended',
    alertPriority: 'warning'
  },
  LOW: {
    level: 'Low',
    color: '#22c55e',
    threshold: 0,
    description: 'Normal monitoring sufficient',
    alertPriority: 'info'
  }
};

/**
 * Common agricultural pests database
 */
export const PEST_DATABASE = {
  APHIDS: {
    id: 'aphids',
    name: 'Aphids',
    scientificName: 'Aphidoidea',
    affectedCrops: ['wheat', 'corn', 'vegetables', 'fruits'],
    favorableConditions: {
      tempMin: 15,
      tempMax: 30,
      humidityMin: 60,
      humidityMax: 90
    },
    seasonalPeak: [4, 5, 6, 9, 10], // April-June, Sept-Oct
    preventiveActions: [
      'Introduce ladybugs as natural predators',
      'Apply neem oil spray',
      'Remove infested plant parts',
      'Maintain proper plant spacing'
    ]
  },
  STEM_BORER: {
    id: 'stem_borer',
    name: 'Stem Borer',
    scientificName: 'Chilo partellus',
    affectedCrops: ['corn', 'sorghum', 'rice', 'sugarcane'],
    favorableConditions: {
      tempMin: 25,
      tempMax: 35,
      humidityMin: 70,
      humidityMax: 95
    },
    seasonalPeak: [6, 7, 8], // June-August
    preventiveActions: [
      'Early planting to avoid peak infestation',
      'Remove and destroy crop residues',
      'Use resistant crop varieties',
      'Apply biological control agents'
    ]
  },
  FALL_ARMYWORM: {
    id: 'fall_armyworm',
    name: 'Fall Armyworm',
    scientificName: 'Spodoptera frugiperda',
    affectedCrops: ['corn', 'sorghum', 'rice', 'cotton'],
    favorableConditions: {
      tempMin: 20,
      tempMax: 35,
      humidityMin: 50,
      humidityMax: 80
    },
    seasonalPeak: [5, 6, 7, 8, 9], // May-September
    preventiveActions: [
      'Scout fields regularly for egg masses',
      'Release Trichogramma parasitoids',
      'Apply Bt-based biopesticides',
      'Intercrop with push-pull companions'
    ]
  },
  RUST: {
    id: 'rust',
    name: 'Rust (Fungal)',
    scientificName: 'Puccinia spp.',
    affectedCrops: ['wheat', 'corn', 'soybeans', 'coffee'],
    favorableConditions: {
      tempMin: 15,
      tempMax: 25,
      humidityMin: 80,
      humidityMax: 100
    },
    seasonalPeak: [3, 4, 5, 10, 11], // Spring and Fall
    preventiveActions: [
      'Plant resistant varieties',
      'Apply fungicides preventively',
      'Improve air circulation between plants',
      'Remove volunteer plants and debris'
    ]
  },
  BLIGHT: {
    id: 'blight',
    name: 'Blight (Bacterial/Fungal)',
    scientificName: 'Various',
    affectedCrops: ['potato', 'tomato', 'rice', 'vegetables'],
    favorableConditions: {
      tempMin: 10,
      tempMax: 25,
      humidityMin: 85,
      humidityMax: 100
    },
    seasonalPeak: [4, 5, 6, 9, 10], // Cool wet seasons
    preventiveActions: [
      'Use disease-free seeds and transplants',
      'Implement crop rotation (3+ years)',
      'Apply copper-based fungicides',
      'Ensure proper drainage'
    ]
  }
};

/**
 * Calculate pest/disease risk score based on weather conditions
 * 
 * @param {Object} weather - Current/forecast weather data
 * @param {Object} pest - Pest information from database
 * @returns {number} Risk score (0-100)
 */
export const calculatePestRisk = (weather, pest) => {
  const { temperature, humidity, precipitation, windSpeed } = weather;
  const { favorableConditions, seasonalPeak } = pest;

  let riskScore = 0;

  // Temperature factor (30% weight)
  if (temperature >= favorableConditions.tempMin && temperature <= favorableConditions.tempMax) {
    const tempOptimal = (favorableConditions.tempMin + favorableConditions.tempMax) / 2;
    const tempDiff = Math.abs(temperature - tempOptimal);
    const tempRange = (favorableConditions.tempMax - favorableConditions.tempMin) / 2;
    riskScore += 30 * (1 - tempDiff / tempRange);
  }

  // Humidity factor (30% weight)
  if (humidity >= favorableConditions.humidityMin && humidity <= favorableConditions.humidityMax) {
    const humOptimal = (favorableConditions.humidityMin + favorableConditions.humidityMax) / 2;
    const humDiff = Math.abs(humidity - humOptimal);
    const humRange = (favorableConditions.humidityMax - favorableConditions.humidityMin) / 2;
    riskScore += 30 * (1 - humDiff / humRange);
  }

  // Seasonal factor (25% weight)
  const currentMonth = new Date().getMonth() + 1;
  if (seasonalPeak.includes(currentMonth)) {
    riskScore += 25;
  } else if (seasonalPeak.includes(currentMonth - 1) || seasonalPeak.includes(currentMonth + 1)) {
    riskScore += 15; // Adjacent months
  }

  // Precipitation factor (10% weight) - wet conditions favor most pests/diseases
  if (precipitation > 5) {
    riskScore += 10 * Math.min(1, precipitation / 20);
  }

  // Wind factor (5% weight) - affects spread
  if (windSpeed > 10) {
    riskScore += 5;
  }

  return Math.min(100, Math.max(0, Math.round(riskScore)));
};

/**
 * Get risk level from score
 * 
 * @param {number} score - Risk score (0-100)
 * @returns {Object} Risk level object
 */
export const getRiskLevel = (score) => {
  if (score >= RISK_LEVELS.HIGH.threshold) return RISK_LEVELS.HIGH;
  if (score >= RISK_LEVELS.MODERATE.threshold) return RISK_LEVELS.MODERATE;
  return RISK_LEVELS.LOW;
};

/**
 * Predict pest/disease risks for a region
 * 
 * @param {Object} params - Prediction parameters
 * @returns {Object} Comprehensive risk assessment
 */
export const predictPestRisks = (params) => {
  const {
    region,
    crops,
    weather,
    weatherForecast = [], // 14-30 day forecast
    historicalOutbreaks = []
  } = params;

  const predictions = [];

  // Analyze each pest/disease for the crops in the region
  Object.values(PEST_DATABASE).forEach(pest => {
    // Check if any crops are affected
    const affectedCrops = crops.filter(crop =>
      pest.affectedCrops.includes(crop.toLowerCase())
    );

    if (affectedCrops.length > 0) {
      // Calculate current risk
      const currentRisk = calculatePestRisk(weather, pest);
      const riskLevel = getRiskLevel(currentRisk);

      // Calculate forecast risks (14-30 days)
      const forecastRisks = weatherForecast.map(day => ({
        date: day.date,
        risk: calculatePestRisk(day, pest)
      }));

      // Find peak risk in forecast
      const peakForecast = forecastRisks.length > 0
        ? forecastRisks.reduce((max, curr) => curr.risk > max.risk ? curr : max, forecastRisks[0])
        : null;

      // Check historical patterns
      const historicalMatch = historicalOutbreaks.filter(outbreak =>
        outbreak.pestId === pest.id &&
        new Date(outbreak.date).getMonth() === new Date().getMonth()
      );

      predictions.push({
        pest: {
          id: pest.id,
          name: pest.name,
          scientificName: pest.scientificName
        },
        affectedCrops,
        currentRisk: {
          score: currentRisk,
          level: riskLevel,
          probability: `${currentRisk}%`
        },
        forecast: {
          days: forecastRisks.length,
          peakRisk: peakForecast,
          trend: calculateTrend(forecastRisks),
          risks: forecastRisks
        },
        historicalContext: {
          previousOutbreaks: historicalMatch.length,
          lastOutbreak: historicalMatch[0]?.date || null
        },
        preventiveActions: pest.preventiveActions,
        recommendedAction: getRecommendedAction(riskLevel, pest)
      });
    }
  });

  // Sort by current risk (highest first)
  predictions.sort((a, b) => b.currentRisk.score - a.currentRisk.score);

  // Generate alerts for high-risk predictions
  const alerts = generatePestAlerts(predictions);

  return {
    region,
    assessmentDate: new Date().toISOString(),
    weatherConditions: weather,
    predictions,
    alerts,
    summary: {
      totalThreats: predictions.length,
      highRisk: predictions.filter(p => p.currentRisk.level.level === 'High').length,
      moderateRisk: predictions.filter(p => p.currentRisk.level.level === 'Moderate').length,
      lowRisk: predictions.filter(p => p.currentRisk.level.level === 'Low').length
    },
    nextReviewDate: calculateNextReviewDate(predictions)
  };
};

/**
 * Calculate risk trend from forecast data
 */
const calculateTrend = (forecastRisks) => {
  if (forecastRisks.length < 2) return 'stable';

  const first = forecastRisks.slice(0, Math.floor(forecastRisks.length / 2));
  const second = forecastRisks.slice(Math.floor(forecastRisks.length / 2));

  const avgFirst = first.reduce((sum, r) => sum + r.risk, 0) / first.length;
  const avgSecond = second.reduce((sum, r) => sum + r.risk, 0) / second.length;

  if (avgSecond > avgFirst + 10) return 'increasing';
  if (avgSecond < avgFirst - 10) return 'decreasing';
  return 'stable';
};

/**
 * Get recommended action based on risk level
 */
const getRecommendedAction = (riskLevel, pest) => {
  if (riskLevel.level === 'High') {
    return {
      urgency: 'immediate',
      actions: [
        `Implement ${pest.preventiveActions[0]}`,
        'Increase field monitoring to daily',
        'Prepare treatment materials',
        'Alert neighboring farms'
      ]
    };
  } else if (riskLevel.level === 'Moderate') {
    return {
      urgency: 'within_week',
      actions: [
        'Increase monitoring frequency',
        'Review preventive options',
        `Consider ${pest.preventiveActions[0]}`
      ]
    };
  }
  return {
    urgency: 'routine',
    actions: ['Continue standard monitoring practices']
  };
};

/**
 * Generate alerts for high-risk predictions
 */
const generatePestAlerts = (predictions) => {
  const alerts = [];

  predictions.forEach(pred => {
    if (pred.currentRisk.level.level === 'High') {
      alerts.push({
        id: `alert-${pred.pest.id}-${Date.now()}`,
        type: 'pest_risk',
        severity: 'critical',
        pestName: pred.pest.name,
        riskScore: pred.currentRisk.score,
        message: `HIGH RISK: ${pred.pest.name} outbreak conditions detected`,
        affectedCrops: pred.affectedCrops.join(', '),
        recommendedAction: pred.recommendedAction.actions[0],
        createdAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() // 24 hours
      });
    } else if (pred.currentRisk.level.level === 'Moderate' && pred.forecast.trend === 'increasing') {
      alerts.push({
        id: `alert-${pred.pest.id}-${Date.now()}`,
        type: 'pest_risk',
        severity: 'warning',
        pestName: pred.pest.name,
        riskScore: pred.currentRisk.score,
        message: `WATCH: ${pred.pest.name} risk increasing over next ${pred.forecast.days} days`,
        affectedCrops: pred.affectedCrops.join(', '),
        recommendedAction: 'Increase monitoring frequency',
        createdAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 72 * 60 * 60 * 1000).toISOString() // 72 hours
      });
    }
  });

  return alerts;
};

/**
 * Calculate next review date based on risk levels
 */
const calculateNextReviewDate = (predictions) => {
  const hasHighRisk = predictions.some(p => p.currentRisk.level.level === 'High');
  const hasModerateRisk = predictions.some(p => p.currentRisk.level.level === 'Moderate');

  let daysUntilReview;
  if (hasHighRisk) daysUntilReview = 1;
  else if (hasModerateRisk) daysUntilReview = 3;
  else daysUntilReview = 7;

  return new Date(Date.now() + daysUntilReview * 24 * 60 * 60 * 1000).toISOString();
};

/**
 * Generate geographic risk map data
 * 
 * @param {Object[]} regions - Array of regions with coordinates and data
 * @returns {Object} GeoJSON-compatible risk map data
 */
export const generateRiskMapData = (regions) => {
  const features = regions.map(region => {
    const prediction = predictPestRisks({
      region: region.name,
      crops: region.crops,
      weather: region.weather
    });

    const highestRisk = prediction.predictions[0];

    return {
      type: 'Feature',
      properties: {
        regionId: region.id,
        regionName: region.name,
        riskScore: highestRisk?.currentRisk.score || 0,
        riskLevel: highestRisk?.currentRisk.level.level || 'Low',
        primaryThreat: highestRisk?.pest.name || 'None',
        color: highestRisk?.currentRisk.level.color || RISK_LEVELS.LOW.color,
        alertCount: prediction.alerts.length
      },
      geometry: region.geometry
    };
  });

  return {
    type: 'FeatureCollection',
    features,
    metadata: {
      generatedAt: new Date().toISOString(),
      totalRegions: regions.length,
      highRiskRegions: features.filter(f => f.properties.riskLevel === 'High').length
    }
  };
};

/**
 * Report an actual outbreak for model improvement
 * 
 * @param {Object} outbreakData - Outbreak information
 * @returns {Object} Processed outbreak report
 */
export const reportOutbreak = (outbreakData) => {
  const {
    pestId,
    region,
    severity,
    affectedArea,
    reportedBy,
    coordinates
  } = outbreakData;

  return {
    id: `outbreak-${Date.now()}`,
    pestId,
    pestName: PEST_DATABASE[pestId.toUpperCase()]?.name || pestId,
    region,
    severity,
    affectedArea,
    coordinates,
    reportedBy,
    reportedAt: new Date().toISOString(),
    status: 'confirmed',
    verificationStatus: 'pending',
    usedForTraining: false // Flag for ML model retraining
  };
};

const pestPredictionService = {
  calculatePestRisk,
  getRiskLevel,
  predictPestRisks,
  generateRiskMapData,
  reportOutbreak,
  RISK_LEVELS,
  PEST_DATABASE
};

export default pestPredictionService;
